/**
 * The arithmetic behind drag-to-reorder.
 *
 * All of it is pure and knows nothing about React or the DOM: it takes numbers
 * describing where the rows are and returns numbers saying where they should
 * be. The hook next door does the listening; this file is what gets tested.
 */

/** A row's resting position, in viewport pixels. */
export type Rect = { top: number; height: number };

/**
 * Move one item to another slot.
 *
 * Returns `null` when the move is impossible (out of range, or nowhere to go)
 * so the caller can skip the write entirely rather than appending an event
 * that changes nothing.
 */
export function moveTo<T>(items: readonly T[], from: number, to: number): T[] | null {
  if (from < 0 || from >= items.length) return null;
  if (to < 0 || to >= items.length || to === from) return null;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

/**
 * Which slot the dragged row currently belongs in.
 *
 * A row claims the slot of any neighbour whose midpoint it has crossed — the
 * leading edge in the direction of travel does the crossing, so the list
 * reflows when the row visually overlaps its neighbour rather than when the
 * finger happens to pass a boundary.
 */
export function dropIndex(rects: readonly Rect[], from: number, dy: number): number {
  const row = rects[from];
  if (!row) return from;
  let to = from;

  if (dy > 0) {
    for (let i = from + 1; i < rects.length; i += 1) {
      const other = rects[i];
      if (other && row.top + row.height + dy > other.top + other.height / 2) to = i;
    }
    return to;
  }

  for (let i = from - 1; i >= 0; i -= 1) {
    const other = rects[i];
    if (other && row.top + dy < other.top + other.height / 2) to = i;
  }
  return to;
}

/**
 * How far each row has to slide to make room, in the same index order.
 *
 * Laid out from the real heights rather than assuming every row is the same
 * size, because completed habits fold to a shorter row and a list of mixed
 * heights would otherwise drift by a few pixels per swap.
 */
export function shiftFor(rects: readonly Rect[], gap: number, from: number, to: number): number[] {
  const order = moveTo(
    rects.map((_, index) => index),
    from,
    to,
  );
  const shifts = rects.map(() => 0);
  if (!order) return shifts;

  let top = rects[0]?.top ?? 0;
  for (const index of order) {
    const rect = rects[index];
    if (!rect) continue;
    shifts[index] = top - rect.top;
    top += rect.height + gap;
  }
  return shifts;
}
